import { memo, useCallback, useRef, useState } from "react";
import Animated, { useAnimatedStyle } from "react-native-reanimated";
import OpacityView from "src/components/OpacityView";
import RoundedButton from "src/components/RoundedButton";
import native from "src/constants/native";
import useKeyboardSV from "src/hooks/useKeyboardSV";
import useEditCoverGalleryStore from "src/screens/(internal)/EditCoverGalleryModal/hooks/useEditCoverGalleryStore";
import { EditCoverAIGenerationTabConfig } from "src/screens/(internal)/EditCoverGalleryModal/types";
import { paddingLarge, paddingXXXL } from "src/styles/spacing";
import { useSafeArea } from "src/swsh-native";
import EditCoverAIPromptInput from "./components/EditCoverAIPromptInput";
import GenerationKeyboardContentWrapper from "./components/GenerationKeyboardContentWrapper";
import GenerationLoading from "./components/GenerationLoading";

const EditCoverAIGenerationContent = ({ config }: { config: EditCoverAIGenerationTabConfig }) => {
	const selectCover = useEditCoverGalleryStore((state) => state.selectCover);
	const [isGenerating, setIsGenerating] = useState(false);
	const textInputRef = useRef<string>("");
	const safeArea = useSafeArea();
	const { keyboardSV } = useKeyboardSV();
	const animatedStyle = useAnimatedStyle(
		() => ({
			transform: [{ translateY: -keyboardSV.value }],
		}),
		[keyboardSV],
	);

	const handleSubmit = useCallback(async () => {
		if (textInputRef.current === "") return;
		setIsGenerating(true);
		try {
			const { generationId, imageUrl } = await config.getGeneratedImage(
				textInputRef.current.trim(),
			);
			selectCover({
				variant: "AIGeneration",
				generationId,
				url: imageUrl,
				config,
			});
		} finally {
			setIsGenerating(false);
		}
	}, [config, selectCover]);

	return (
		<Animated.View
			style={[
				{
					flex: 1,
				},
				animatedStyle,
			]}
		>
			<GenerationKeyboardContentWrapper
				style={{
					paddingTop: paddingLarge,
					paddingBottom: safeArea.bottom + paddingLarge,
					width: "100%",
					flex: 1,
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<OpacityView
					key={`${isGenerating}`}
					exiting={false}
					duration={isGenerating ? 2000 : undefined}
					style={{
						paddingBottom: paddingXXXL,
						width: "100%",
						flex: 1,
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					{isGenerating ? (
						<GenerationLoading />
					) : (
						<EditCoverAIPromptInput
							initialValue={textInputRef.current}
							// Only want auto focus if text is empty at the time of rendering
							autoFocus={!textInputRef.current && native}
							onChangeText={(text) => {
								textInputRef.current = text;
							}}
							onSubmit={handleSubmit}
						/>
					)}
				</OpacityView>
				<RoundedButton onPress={handleSubmit} disabled={isGenerating}>
					Generate!
				</RoundedButton>
			</GenerationKeyboardContentWrapper>
		</Animated.View>
	);
};
export default memo(EditCoverAIGenerationContent);
